import type { OperationVariables, TypedDocumentNode } from "@apollo/client";

/**
 * How the browser authenticates with the Storefront API of the previewed
 * store. The org's own store uses its public `storefrontToken`. A demo URL
 * uses tokenless access.
 */
export type StorefrontCredential =
  | { kind: "storefrontToken"; token: string }
  | { kind: "tokenless" };

/**
 * One Storefront API query to one store. The typed document fixes the result
 * and the variables, so a caller does not give them by hand.
 */
export interface StorefrontRequest<
  TData,
  TVariables extends OperationVariables,
> {
  /** The myshopify domain that the preview reports in `ig:shopify`. */
  shop: string;
  credential: StorefrontCredential;
  document: TypedDocumentNode<TData, TVariables>;
  /** Checked against the document's variables, not used to infer them. */
  variables: NoInfer<TVariables>;
}

/**
 * The store refused a tokenless request because its Online Store channel is
 * locked: the store has a storefront password. A token request is not locked.
 */
export class StorefrontChannelLockedError extends Error {
  constructor(shop: string) {
    super(`The Online Store channel of ${shop} is locked`);
    this.name = "StorefrontChannelLockedError";
  }
}

/**
 * The Navigator's only way to read the Storefront API. The Navigator provider
 * receives it as a prop, so tests pass a fake without module mocking.
 * The promise rejects when the store gives no data: with a
 * `StorefrontChannelLockedError` when the Online Store channel is locked, and
 * with any other error for all other failures.
 */
export interface StorefrontClient {
  query<TData, TVariables extends OperationVariables>(
    request: StorefrontRequest<TData, TVariables>
  ): Promise<TData>;
}
